import React, { useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import RedoIcon from "@mui/icons-material/Redo";
import RemoveIcon from "@mui/icons-material/Remove";
import UndoIcon from "@mui/icons-material/Undo";
import { Box, MenuItem, IconButton as MuiIconButton, Select, Typography } from "@mui/material";
import type { DroppingItem } from "components/Report-generator/context/ReportGenerator-context";
import { useMeasuredTableHeights } from "components/Report-generator/hooks/use-measured-table-heights";
import {
  computeDataFreshnessGridH,
  computeTableGridH,
  DRAG_HANDLE_HEIGHT,
  gridItemWidth,
  previewCardWidth
} from "components/Report-generator/utils/gridUtils";
import type { LayoutItem } from "components/Report-generator/utils/gridUtils";
import { getPageMetrics } from "components/Report-generator/utils/pageMetrics";
import { assignRowPaths, splitPagesForOverflow } from "components/Report-generator/utils/splitTablePages";
import type {
  CanvasCard,
  CardType,
  DataTableCardData,
  PageLayout,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  SizePreset,
  TableRow
} from "store/types";
import { GRID_CONSTANTS } from "store/types/reports/report-generator";

import { ToggleButtons } from "../ToggleButtons";
import { PAGE_LAYOUT_OPTIONS, PageView } from "./PageView";

const EMPTY_SET: Set<string> = new Set();

const ORIENTATION_OPTIONS: { value: ReportOrientation; label: string }[] = [
  { value: "portrait", label: "Portrait" },
  { value: "landscape", label: "Landscape" }
];

// Zoom bounds/step — single source of truth for the +/- controls and the select options
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1;
const ZOOM_STEP = 0.1;
const ZOOM_OPTIONS = Array.from(
  { length: Math.round((ZOOM_MAX - ZOOM_MIN) / ZOOM_STEP) + 1 },
  (_, i) => Math.round((ZOOM_MIN + i * ZOOM_STEP) * 100) / 100
);

interface Props {
  cards: CanvasCard[];
  pages: ReportPage[];
  selectedCardId: string;
  reportMeta: ReportMeta;
  orientation: ReportOrientation;
  onOrientationChange: (value: ReportOrientation) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onZoomChange: (zoom: number) => void;
  isResizing: boolean;
  droppingItem: DroppingItem | null;
  onResizingChange: (resizing: boolean) => void;
  onUpdateMeta: (updates: Partial<ReportMeta>) => void;
  onLogoFileChange: (file: File | null) => void;
  onSelectCard: (id: string) => void;
  onRemoveCard: (id: string) => void;
  onDuplicateCard: (id: string) => void;
  onUpdateCard: (id: string, updates: Partial<Omit<CanvasCard, "id" | "type">>) => void;
  onPresetChange: (id: string, preset: SizePreset) => void;
  expandedRows: Record<string, Set<string>>;
  onToggleExpand: (cardId: string, rowKey: string) => void;
  onAddPage: (layout?: PageLayout) => void;
  onRemovePage: (page: number) => void;
  onUpdatePage: (pageIndex: number, updates: Partial<ReportPage>) => void;
  onLayoutChange: (pageIndex: number, layout: LayoutItem[]) => void;
  onDropCard: (type: CardType, pageIndex: number, gridX: number, gridY: number, resolvedLayout?: LayoutItem[]) => void;
  onOpenSettings: (id: string) => void;
  zoom: number;
}

export const Canvas: React.FC<Props> = ({
  cards,
  pages,
  selectedCardId,
  reportMeta,
  orientation,
  onOrientationChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onZoomChange,
  isResizing,
  droppingItem,
  onResizingChange,
  onUpdateMeta,
  onLogoFileChange,
  onSelectCard,
  onRemoveCard,
  onDuplicateCard,
  onUpdateCard,
  onPresetChange,
  expandedRows,
  onToggleExpand,
  onAddPage,
  onRemovePage,
  onUpdatePage,
  onLayoutChange,
  onDropCard,
  onOpenSettings,
  zoom
}) => {
  const [showAddMenu, setShowAddMenu] = React.useState(false);
  const [zoomOpen, setZoomOpen] = React.useState(false);
  const { width: pageWidth, height: pageHeight } = getPageMetrics(orientation);

  // Tag every table row with a stable __path so its DOM-measured height can be
  // looked up during pagination, then measure the real heights at canvas width.
  const cardsWithPaths = useMemo(
    () =>
      cards.map((card) => {
        if (card.config.type !== "table" || !card.data) return card;
        const data = card.data as DataTableCardData;
        return { ...card, data: { ...data, data: assignRowPaths(data.data) } };
      }),
    [cards]
  );
  const tableCards = useMemo(
    () => cardsWithPaths.filter((c) => c.config.type === "table" && !!c.data),
    [cardsWithPaths]
  );
  // Measure at the preview's table width so the canvas paginates row-for-row
  // identically to the exported PDF (the canvas renders a touch wider, but the
  // row counts per page match).
  const { measured, measureNode } = useMeasuredTableHeights(tableCards, pageWidth, previewCardWidth, orientation);

  const { resolvedPages, resolvedMeta, resolvedPageCount, cardOffsets, originalCardIds } = useMemo(() => {
    // Exact DOM-measured heights when available; undefined falls back to the estimate.
    const getRowHeight = (cardId: string, row: TableRow) => measured?.get(cardId)?.rows.get(row.__path ?? "");
    const getHeaderHeight = (cardId: string) => measured?.get(cardId)?.header;

    // Pre-process: compute content-driven gridH for table cards
    const adjustedCards = cardsWithPaths.map((card) => {
      // Data Freshness cards size to one grid row per data entry.
      if (card.config.type === "dataFreshness" && card.data) {
        const gridH = computeDataFreshnessGridH(card);
        return gridH === card.gridH ? card : { ...card, gridH };
      }
      if (card.config.type !== "table" || !card.data) return card;
      const expanded = expandedRows[card.id] ?? EMPTY_SET;
      const contentGridH = computeTableGridH(
        card,
        expanded,
        gridItemWidth(pageWidth, card.gridW),
        undefined,
        undefined,
        DRAG_HANDLE_HEIGHT,
        true,
        { getRowHeight: (row) => getRowHeight(card.id, row), getHeaderHeight: () => getHeaderHeight(card.id) }
      );
      if (contentGridH === card.gridH) return card;
      return { ...card, gridH: contentGridH };
    });

    const rawGroupedPages: CanvasCard[][] = Array.from({ length: pages.length }, (_, i) =>
      adjustedCards.filter((c) => c.page === i)
    );
    const result = splitPagesForOverflow(rawGroupedPages, pages, pageHeight, pageWidth, expandedRows, {
      contentPadding: 56,
      isEditMode: true,
      getRowHeight,
      getHeaderHeight,
      // Reserve the 28px card handle on the table's first page in the canvas.
      tableTopChromePx: DRAG_HANDLE_HEIGHT
    });

    // Post-process: recompute gridH for each table segment based on its actual
    // (sliced) data. Continuation cards inherit the original card's full-content
    // gridH via spread, so they must be resized to fit only their own rows.
    const sizedPages = result.pages.map((pageCards) =>
      pageCards.map((card) => {
        if (card.config.type !== "table" || !card.data) return card;
        const isContinuation = !!result.originalCardIds[card.id];
        const origId = result.originalCardIds[card.id] ?? card.id;
        const expanded = expandedRows[origId] ?? EMPTY_SET;
        // Use minGridH=1 so continuation segments with few rows aren't forced to the default minimum.
        // Pass index offsets so row keys match the ones stored in expandedRows (e.g. "root-5" not "root-0").
        // Non-continuation cards have a 28px drag handle in edit mode that reduces content area.
        const offsets = result.cardOffsets[card.id];
        const overhead = isContinuation ? 0 : DRAG_HANDLE_HEIGHT;
        const segmentGridH = computeTableGridH(
          card,
          expanded,
          gridItemWidth(pageWidth, card.gridW),
          1,
          offsets,
          overhead,
          true,
          { getRowHeight: (row) => getRowHeight(origId, row), getHeaderHeight: () => getHeaderHeight(origId) }
        );
        if (segmentGridH === card.gridH) return card;
        return { ...card, gridH: segmentGridH };
      })
    );

    // Extend splitting tables' gridH on primary pages to reserve remaining space.
    // Prevents RGL from allowing drops below a table that continues on the next page.
    const splittingCardIds = new Set(Object.values(result.originalCardIds));
    const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;

    for (let resolvedIdx = 0; resolvedIdx < sizedPages.length; resolvedIdx++) {
      const meta = result.pageMeta[resolvedIdx];
      // Skip table-end pages: the table ends there, so it must NOT be stretched to
      // fill the page — that would remove the drop zone and overlap dropped cards.
      if (meta.isContinuation || meta.isSpillover || meta.isTableEnd) continue;

      const pgCards = sizedPages[resolvedIdx];
      const splittingTableIdx = pgCards.findIndex((c) => c.config.type === "table" && splittingCardIds.has(c.id));
      if (splittingTableIdx === -1) continue;

      const headerH = meta.layout === "header-content" ? 180 : meta.layout === "section" ? 150 : 0;
      const contentPadV = 32;
      const availH = pageHeight - headerH - contentPadV;
      const maxRows = Math.floor((availH + GRID_CONSTANTS.GAP) / cellPlusGap);

      const tableCard = pgCards[splittingTableIdx];
      const extendedGridH = maxRows - tableCard.gridY;

      if (extendedGridH > tableCard.gridH) {
        sizedPages[resolvedIdx] = pgCards.map((c, i) => (i === splittingTableIdx ? { ...c, gridH: extendedGridH } : c));
      }
    }

    return {
      resolvedPages: sizedPages,
      resolvedMeta: result.pageMeta,
      resolvedPageCount: sizedPages.length,
      cardOffsets: result.cardOffsets,
      originalCardIds: result.originalCardIds
    };
  }, [cardsWithPaths, pages, pageHeight, pageWidth, expandedRows, measured]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: "#DBE8F5",
        borderRadius: "8px",
        overflow: "hidden"
      }}
    >
      {/* Off-screen tables measured for pixel-perfect pagination. */}
      {measureNode}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          p: "12px 8px",
          flexShrink: 0
        }}
      >
        <ToggleButtons options={ORIENTATION_OPTIONS} value={orientation} onChange={onOrientationChange} />

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
          <MuiIconButton
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo"
            size="sm"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #dbe8f5",
              bgcolor: "#FFFFFF",
              color: "#496682",
              "&:hover": { bgcolor: "#f6f8fc" },
              "&.Mui-disabled": { bgcolor: "#FFFFFF" }
            }}
          >
            <UndoIcon sx={{ fontSize: 16 }} />
          </MuiIconButton>
          <MuiIconButton
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo"
            size="sm"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #dbe8f5",
              bgcolor: "#FFFFFF",
              color: "#496682",
              "&:hover": { bgcolor: "#f6f8fc" },
              "&.Mui-disabled": { bgcolor: "#FFFFFF" }
            }}
          >
            <RedoIcon sx={{ fontSize: 16 }} />
          </MuiIconButton>

          <Box sx={{ width: "1px", height: 24, bgcolor: "#92A7BC", flexShrink: 0 }} />

          <MuiIconButton
            onClick={() => onZoomChange(Math.max(ZOOM_MIN, Math.round((zoom - ZOOM_STEP) * 100) / 100))}
            disabled={zoom <= ZOOM_MIN}
            title="Zoom out"
            size="sm"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #dbe8f5",
              bgcolor: "#FFFFFF",
              color: "#496682",
              fontSize: 16,
              "&:hover": { bgcolor: "#f6f8fc" },
              "&.Mui-disabled": { bgcolor: "#FFFFFF" }
            }}
          >
            <RemoveIcon sx={{ fontSize: 16 }} />
          </MuiIconButton>
          <Select
            value={zoom}
            open={zoomOpen}
            onOpen={() => setZoomOpen(true)}
            onClose={() => setZoomOpen(false)}
            onChange={(e) => onZoomChange(Number(e.target.value))}
            title="Zoom"
            renderValue={() => `${Math.round(zoom * 100)}%`}
            IconComponent={zoomOpen ? ArrowDropUpIcon : ArrowDropDownIcon}
            MenuProps={{
              PaperProps: { sx: { borderRadius: "8px", boxShadow: "0px 4px 20px rgba(0,0,0,0.1)" } }
            }}
            sx={{
              width: 72,
              height: 32,
              borderRadius: "8px",
              bgcolor: "transparent",
              color: "#496682",
              fontSize: 14,
              fontWeight: 400,
              lineHeight: "16px",
              fontFamily: "inherit",
              "& .MuiSelect-select": {
                padding: "8px !important",
                minHeight: "0 !important",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start"
              },
              "& .MuiSelect-icon": { color: "#496682", right: 4 },
              // We swap the icon ourselves via IconComponent — cancel MUI's default 180° flip
              "& .MuiSelect-iconOpen": { transform: "none" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#496682" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#496682" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#496682", borderWidth: "1px" }
            }}
          >
            {ZOOM_OPTIONS.map((z) => (
              <MenuItem
                key={z}
                value={z}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#496682",
                  justifyContent: "center",
                  p: "8px 12px",
                  "&:hover": { bgcolor: "#ECF1F8" },
                  "&.Mui-focusVisible": { bgcolor: "#ECF1F8" },
                  "&.Mui-selected": { bgcolor: "#DBE8F5 !important" },
                  "&.Mui-selected:hover": { bgcolor: "#DBE8F5 !important" },
                  "&.Mui-selected.Mui-focusVisible": { bgcolor: "#DBE8F5 !important" }
                }}
              >
                {Math.round(z * 100)}%
              </MenuItem>
            ))}
          </Select>
          <MuiIconButton
            onClick={() => onZoomChange(Math.min(ZOOM_MAX, Math.round((zoom + ZOOM_STEP) * 100) / 100))}
            disabled={zoom >= ZOOM_MAX}
            title="Zoom in"
            size="sm"
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #dbe8f5",
              bgcolor: "#FFFFFF",
              color: "#496682",
              fontSize: 16,
              "&:hover": { bgcolor: "#f6f8fc" },
              "&.Mui-disabled": { bgcolor: "#FFFFFF" }
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </MuiIconButton>
        </Box>
      </Box>

      <Box sx={{ height: "1px", bgcolor: "#FFFFFF", flexShrink: 0, mx: "8px" }} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto", p: "56px 32px 0px" }}>
        <Box
          sx={{
            width: "fit-content",
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "56px",
            zoom
          }}
        >
          {resolvedPages.map((pageCards, resolvedIndex) => {
            const meta = resolvedMeta[resolvedIndex];

            if (meta.isContinuation) {
              return (
                <PageView
                  key={`cont-${resolvedIndex}`}
                  pageIndex={resolvedIndex}
                  pageCount={resolvedPageCount}
                  originalPageIndex={meta.originalPageIndex}
                  page={meta}
                  pageCards={pageCards}
                  allCards={cards}
                  pageWidth={pageWidth}
                  pageHeight={pageHeight}
                  selectedCardId={selectedCardId}
                  reportMeta={reportMeta}
                  orientation={orientation}
                  isResizing={isResizing}
                  droppingItem={droppingItem}
                  isContinuation
                  expandedRows={expandedRows}
                  onToggleExpand={onToggleExpand}
                  cardOffsets={cardOffsets}
                  originalCardIds={originalCardIds}
                  onResizingChange={onResizingChange}
                  onUpdateMeta={onUpdateMeta}
                  onLogoFileChange={onLogoFileChange}
                  onSelectCard={onSelectCard}
                  onRemoveCard={onRemoveCard}
                  onDuplicateCard={onDuplicateCard}
                  onUpdateCard={onUpdateCard}
                  onPresetChange={onPresetChange}
                  onRemovePage={() => {}}
                  onUpdatePage={() => {}}
                  onLayoutChange={onLayoutChange}
                  onDropCard={onDropCard}
                  onOpenSettings={onOpenSettings}
                  zoom={zoom}
                />
              );
            }

            if (meta.isSpillover) {
              return (
                <PageView
                  key={`spillover-${resolvedIndex}`}
                  pageIndex={resolvedIndex}
                  pageCount={resolvedPageCount}
                  originalPageIndex={meta.originalPageIndex}
                  page={meta}
                  pageCards={pageCards}
                  allCards={cards}
                  pageWidth={pageWidth}
                  pageHeight={pageHeight}
                  selectedCardId={selectedCardId}
                  reportMeta={reportMeta}
                  orientation={orientation}
                  isResizing={isResizing}
                  droppingItem={droppingItem}
                  expandedRows={expandedRows}
                  onToggleExpand={onToggleExpand}
                  cardOffsets={cardOffsets}
                  originalCardIds={originalCardIds}
                  onResizingChange={onResizingChange}
                  onUpdateMeta={onUpdateMeta}
                  onLogoFileChange={onLogoFileChange}
                  onSelectCard={onSelectCard}
                  onRemoveCard={onRemoveCard}
                  onDuplicateCard={onDuplicateCard}
                  onUpdateCard={onUpdateCard}
                  onPresetChange={onPresetChange}
                  onRemovePage={() => {}}
                  onUpdatePage={() => {}}
                  onLayoutChange={onLayoutChange}
                  onDropCard={onDropCard}
                  onOpenSettings={onOpenSettings}
                  zoom={zoom}
                />
              );
            }

            return (
              <PageView
                key={`page-${meta.originalPageIndex}`}
                pageIndex={resolvedIndex}
                pageCount={resolvedPageCount}
                originalPageIndex={meta.originalPageIndex}
                page={pages[meta.originalPageIndex]}
                pageCards={pageCards}
                allCards={cards}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
                selectedCardId={selectedCardId}
                reportMeta={reportMeta}
                orientation={orientation}
                isResizing={isResizing}
                droppingItem={droppingItem}
                isTableEnd={meta.isTableEnd}
                expandedRows={expandedRows}
                onToggleExpand={onToggleExpand}
                cardOffsets={cardOffsets}
                originalCardIds={originalCardIds}
                onResizingChange={onResizingChange}
                onUpdateMeta={onUpdateMeta}
                onLogoFileChange={onLogoFileChange}
                onSelectCard={onSelectCard}
                onRemoveCard={onRemoveCard}
                onDuplicateCard={onDuplicateCard}
                onUpdateCard={onUpdateCard}
                onPresetChange={onPresetChange}
                onRemovePage={() => onRemovePage(meta.originalPageIndex)}
                onUpdatePage={(updates) => onUpdatePage(meta.originalPageIndex, updates)}
                onLayoutChange={onLayoutChange}
                onDropCard={onDropCard}
                onOpenSettings={onOpenSettings}
                zoom={zoom}
              />
            );
          })}

          <Box sx={{ position: "relative", width: pageWidth, pb: 10, mt: "-22px" }}>
            <Box
              component="button"
              onClick={() => setShowAddMenu((v) => !v)}
              sx={{
                width: "100%",
                p: "18px 0",
                border: "2px dashed rgba(7,31,53,0.25)",
                borderRadius: "10px",
                bgcolor: "transparent",
                color: "rgba(7,31,53,0.55)",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer"
              }}
            >
              Add page
            </Box>
            {showAddMenu && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  mb: 1,
                  bgcolor: "#FFFFFF",
                  borderRadius: "10px",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                  border: "1px solid #dbe8f5",
                  p: 1,
                  display: "flex",
                  gap: "6px",
                  zIndex: 20,
                  flexWrap: "wrap",
                  justifyContent: "center"
                }}
              >
                {PAGE_LAYOUT_OPTIONS.map((option) => (
                  <Box
                    key={option.value}
                    component="button"
                    onClick={() => {
                      onAddPage(option.value);
                      setShowAddMenu(false);
                    }}
                    sx={{
                      p: "10px 14px",
                      border: "1px solid #dbe8f5",
                      borderRadius: "8px",
                      bgcolor: "#FAFCFE",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "4px",
                      minWidth: 92
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#071f35" }}>{option.tag}</Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#071f35" }}>{option.label}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
